import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '../notification.service';
import { RejectCommentDialogComponent } from '../../moderation/comment-moderation/reject-comment-dialog/reject-comment-dialog.component';
import { DomainMarketLang } from '../../shared/models/comments/comment.model';
import { BehaviorSubject, defaultIfEmpty, Observable, of, Subject } from 'rxjs';
import { TextPopinComponent } from '../../shared/components/text-popin/text-popin.component';
import { catchError, delay, filter, mergeMap, takeUntil, tap } from 'rxjs/operators';
import { SelectionModel } from '@angular/cdk/collections';
import { LocalizedKeyValue } from '../../shared/models/dataref.model';
import { CommentService } from './comment.service';
import { CommentSearchContent } from '../../shared/models/search.model';

/**
 * Enumeration for action types used in comment moderation.
 */
export enum ActionType {
  APPROVE = 'APPROVE',
  REJECT = 'REJECT',
  DELETE = 'DELETE',
}

/**
 * Interface for the response type of comment actions.
 */
export interface CommentResponseType {
  validItems?: ValidItem[];
  errorItems?: ErrorItem[];
}

/**
 * Interface for the response type of comment actions.
 */
export interface ValidItem {
  identifier: {
    functionalId: string;
  };
  status: 'PUBLISHED' | 'REJECTED' | 'DELETED';
}

/**
 * Interface for error items in a comment action response.
 */
export interface ErrorItem {
  resourceIds: [
    {
      functionalId: string;
    }
  ];
  errorType: string;
}

enum MessageType {
  ERROR_APPROVE = 'ERROR.APPROVE',
  ERROR_REJECT = 'ERROR.REJECT',
  ERROR_DELETE = 'ERROR.DELETE',
  ERROR_FORBIDDEN = 'ERROR.FORBIDDEN',
  ERROR_DEFAULT = 'ERROR.DEFAULT',
  SUCCESS_APPROVE_WITH_ERRORS = 'SUCCESS.APPROVE_WITH_ERRORS',
  SUCCESS_REJECT_WITH_ERRORS = 'SUCCESS.REJECT_WITH_ERRORS',
  SUCCESS_DELETE_WITH_ERRORS = 'SUCCESS.DELETE_WITH_ERRORS',
  SUCCESS_APPROVE_WITHOUT_ERRORS = 'SUCCESS.APPROVE_WITHOUT_ERRORS',
  SUCCESS_REJECT_WITHOUT_ERRORS = 'SUCCESS.REJECT_WITHOUT_ERRORS',
  SUCCESS_DELETE_WITHOUT_ERRORS = 'SUCCESS.DELETE_WITHOUT_ERRORS',
}

const actionTypeToError: { [key in ActionType]: MessageType } = {
  [ActionType.APPROVE]: MessageType.ERROR_APPROVE,
  [ActionType.REJECT]: MessageType.ERROR_REJECT,
  [ActionType.DELETE]: MessageType.SUCCESS_DELETE_WITHOUT_ERRORS,
};

const actionTypeToSuccessWithErrors: { [key in ActionType]: MessageType } = {
  [ActionType.APPROVE]: MessageType.SUCCESS_APPROVE_WITH_ERRORS,
  [ActionType.REJECT]: MessageType.SUCCESS_REJECT_WITH_ERRORS,
  [ActionType.DELETE]: MessageType.SUCCESS_DELETE_WITH_ERRORS,
};

const statusToSuccessWithoutErrors: { [key: string]: MessageType } = {
  PUBLISHED: MessageType.SUCCESS_APPROVE_WITHOUT_ERRORS,
  REJECTED: MessageType.SUCCESS_REJECT_WITHOUT_ERRORS,
  DELETED: MessageType.SUCCESS_DELETE_WITHOUT_ERRORS,
};

/**
 * Injectable service for handling comment moderation.
 */
@Injectable({
  providedIn: 'root',
})
export class CommentModerationService implements OnDestroy {
  elementStylesSource = new BehaviorSubject<{ [commentId: string]: string }>({});
  elementStyles$ = this.elementStylesSource.asObservable();

  reloadCommentsSource = new Subject<void>();
  reloadComments$ = this.reloadCommentsSource.asObservable();

  private destroy$ = new Subject<void>();

  /**
   * Constructs the CommentModerationService.
   * @param dialog - MatDialog instance.
   * @param translateService - TranslateService instance.
   * @param notificationService - NotificationService instance.
   * @param commentService - CommentService instance.
   */
  constructor(
    private dialog: MatDialog,
    private translateService: TranslateService,
    private notificationService: NotificationService,
    private commentService: CommentService
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Handles the rejection of comments.
   */
  rejectComments(
    selection: SelectionModel<CommentSearchContent>,
    domainMarketLang: DomainMarketLang,
    commentStatuses: LocalizedKeyValue[],
    commentReasons: LocalizedKeyValue[]
  ): Observable<CommentResponseType | null> {
    const dialogRef = this.dialog.open(RejectCommentDialogComponent, {
      width: '600px',
      height: '300px',
      disableClose: true,
      data: {
        comments: selection.selected,
        commentReasons,
        market: domainMarketLang.market,
        lang: domainMarketLang.lang,
      },
    });

    return dialogRef.afterClosed().pipe(
      filter((reason: string) => !!reason),
      mergeMap((reason) => {
        const selectedComments: CommentSearchContent[] = selection.selected;
        const rejectObservable: Observable<CommentResponseType | null> = this.commentService.batchReject(selectedComments, reason);
        return this.handleCommentUpdate(selection, rejectObservable, ActionType.REJECT);
      }),
      defaultIfEmpty(null),
      takeUntil(this.destroy$)
    );
  }

  /**
   * Handles the deletion of comments.
   */
  deleteComments(selection: SelectionModel<CommentSearchContent>): Observable<CommentResponseType | null> {
    const selectedComments: CommentSearchContent[] = selection.selected;
    const dialogRef = this.dialog.open(TextPopinComponent, {
      disableClose: true,
      data: {
        title: 'COMMENTS.SEARCH.MSG.MODAL_DELETE_TITLE',
        text: 'COMMENTS.SEARCH.MSG.MODAL_DELETE_MSG',
        cancelBtn: 'NO',
        validateBtn: 'YES',
      },
    });

    return dialogRef.afterClosed().pipe(
      filter((isDelete: boolean) => isDelete),
      mergeMap(() => {
        const deleteObservable: Observable<CommentResponseType | null> = this.commentService.batchDelete(selectedComments);
        return this.handleCommentUpdate(selection, deleteObservable, ActionType.DELETE);
      }),
      defaultIfEmpty(null),
      takeUntil(this.destroy$)
    );
  }

  /**
   * Handles the deletion of comments.
   */
  approveComments(selection: SelectionModel<CommentSearchContent>): void {
    const selectedComments: CommentSearchContent[] = selection.selected;
    const approveObservable: Observable<CommentResponseType | null> = this.commentService.batchApprove(selectedComments);
    this.handleCommentUpdate(selection, approveObservable, ActionType.APPROVE).subscribe();
  }

  /**
   * Private method to handle comment update.
   */
  private handleCommentUpdate(
    selection: SelectionModel<CommentSearchContent>,
    observable: Observable<CommentResponseType | null>,
    actionType: ActionType
  ): Observable<CommentResponseType | null> {
    return observable.pipe(
      tap((response: CommentResponseType | null) => {
        if (response === null || response === undefined) {
          if (actionType === ActionType.DELETE) {
            this.displayNotification(null, ActionType.DELETE);
            return;
          }
        }
        const updatedStyles: { [key: string]: string } = {};

        if (response) {
          response.validItems?.forEach((validItem: ValidItem) => {
            updatedStyles[validItem.identifier.functionalId] = 'approved';
          });

          response.errorItems?.forEach((errorItem: ErrorItem) => {
            updatedStyles[errorItem.resourceIds[0].functionalId] = 'error';
          });
        }

        this.elementStylesSource.next(updatedStyles);
        this.displayNotification(response, actionType);
      }),
      delay(2000),
      tap(() => {
        const clearedStyles = { ...this.elementStylesSource.value };
        selection.selected.forEach((comment: CommentSearchContent) => {
          clearedStyles[comment.identifier.functionalId] = '';
        });
        this.elementStylesSource.next(clearedStyles);
      }),
      catchError(() => {
        this.notificationService.sendError(this.translateService.instant('COMMENTS.SEARCH.MSG.ERROR.DEFAULT'));
        return of(null);
      }),
      tap(() => {
        this.reloadCommentsSource.next();
        selection.clear();
      }),
      takeUntil(this.destroy$)
    );
  }

  /**
   * Private method to display a notification.
   */
  private displayNotification(response: CommentResponseType | null, actionType: ActionType): void {
    const messageType = this.determineMessageType(response, actionType);
    const messagePath = `COMMENTS.SEARCH.MSG.${messageType}`;
    let message: string;

    if (response?.errorItems) {
      message = this.translateService.instant(messagePath, { number: response.errorItems.length });
    } else if (!response && actionType === ActionType.DELETE) {
      message = this.translateService.instant('COMMENTS.SEARCH.MSG.SUCCESS.DELETE_WITHOUT_ERRORS');
    } else {
      message = this.translateService.instant(messagePath);
    }

    if (messageType.startsWith('SUCCESS')) {
      this.notificationService.sendSuccess(message);
    } else {
      this.notificationService.sendError(message);
    }
  }

  /**
   * Private method to determine the message type.
   */
  /**
   * Determines the message type based on the action type and response.
   *
   * @param response - Comment response type or null
   * @param actionType - Type of action performed
   * @returns Message type as a string
   */
  private determineMessageType(response: CommentResponseType | null, actionType: ActionType): string {
    if (!response) {
      return this.getMessageTypeForNoResponse(actionType);
    }

    if (this.hasBothValidAndErrorItems(response)) {
      return this.getMessageTypeForMixedItems(actionType);
    }

    if (this.hasOnlyValidItems(response)) {
      return this.getMessageTypeForValidItems(response);
    }

    if (this.hasOnlyErrorItems(response)) {
      return this.getMessageTypeForErrorItems(response, actionType);
    }

    return MessageType.ERROR_DEFAULT;
  }

  /**
   * Gets the message type when there is no response.
   */
  private getMessageTypeForNoResponse(actionType: ActionType): string {
    return actionTypeToError[actionType] || MessageType.ERROR_DEFAULT;
  }

  /**
   * Checks if the response has both valid and error items.
   */
  private hasBothValidAndErrorItems(response: CommentResponseType): boolean {
    return Boolean(response.validItems?.length && response.errorItems?.length);
  }

  /**
   * Gets the message type when the response has both valid and error items.
   */
  private getMessageTypeForMixedItems(actionType: ActionType): string {
    return actionTypeToSuccessWithErrors[actionType] || MessageType.ERROR_DEFAULT;
  }

  /**
   * Checks if the response has only valid items.
   */
  private hasOnlyValidItems(response: CommentResponseType): boolean {
    return Boolean(response.validItems?.length);
  }

  /**
   * Gets the message type when the response has only valid items.
   */
  private getMessageTypeForValidItems(response: CommentResponseType): string {
    const item = response.validItems![0];
    return statusToSuccessWithoutErrors[item.status] || MessageType.ERROR_DEFAULT;
  }

  /**
   * Checks if the response has only error items.
   */
  private hasOnlyErrorItems(response: CommentResponseType): boolean {
    return Boolean(response.errorItems?.length);
  }

  /**
   * Gets the message type when the response has only error items.
   */
  private getMessageTypeForErrorItems(response: CommentResponseType, actionType: ActionType): string {
    const errorType = response.errorItems![0].errorType;
    if (errorType === 'NOT_FOUND') {
      return actionTypeToError[actionType] || MessageType.ERROR_DEFAULT;
    }
    if (errorType === 'FORBIDDEN') {
      return MessageType.ERROR_FORBIDDEN;
    }
    return MessageType.ERROR_DEFAULT;
  }
}
